import TestAiProviderConnection from '@Application/Ai/AiConfiguration/Providers/Commands/TestAiProviderConnection';
import testAiProviderConnection from '@Application/Ai/AiConfiguration/Providers/testAiProviderConnection';
import { PermissionEnum } from '@Application/Security/Roles';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import { openProviderSidebar } from '@UI/Pages/Administration/AiEngines/Signals/providerSidebarSignal';
import { setProviderToDelete } from '@UI/Pages/Administration/AiEngines/Signals/providerToDeleteSignal';
import useHasPermission from '@UI/Providers/PermissionProvider/useHasPermission';
import type AiProviderView from '@UI/Store/AiConfiguration/Views/AiProviderView';
import { css } from '@emotion/react';
import * as React from 'react';
import { useDispatch } from '@verclam/synapse';
import { Button } from '@verclam/ui-kit';

const actionsRowStyle = css`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	flex-wrap: wrap;
`;

type Props = {
	readonly provider: AiProviderView;
};

/**
 * Leaf action component: it opens the edit sidebar, requests the delete confirmation and dispatches
 * the connection test itself, purely through the signals' setters and `useDispatch` — no callback
 * passed up in props. "Modifier" and "Tester la connexion" require `AI_PROVIDER_WRITE` — the
 * connection test spends the account's key and persists its result, which makes it a write, exactly
 * as the server guard requires. "Supprimer" requires `AI_PROVIDER_DELETE`. Buttons carry visible text,
 * rendered as `children` rather than `label`: the kit resolves the accessible name from `label` first,
 * which would shadow the explicit `aria-label` naming the provider — `children` leaves that resolution
 * to `aria-label`.
 *
 * The outer `<div>` is not decorative: it is the actions track of the card's subgrid, and stays
 * rendered even with no button inside — omitting it would shift every following card's track out of
 * alignment with this one.
 */
const ProviderCardActions: React.FC<Props> = ({ provider }) => {
	const dispatch = useDispatch();
	const canWrite = useHasPermission(PermissionEnum.AI_PROVIDER_WRITE);
	const canDelete = useHasPermission(PermissionEnum.AI_PROVIDER_DELETE);

	const handleEditClick = React.useCallback(() => {
		openProviderSidebar(provider.id);
	}, [provider.id]);

	const handleDeleteClick = React.useCallback(() => {
		setProviderToDelete(provider.id);
	}, [provider.id]);

	const handleTestClick = React.useCallback(() => {
		dispatch(
			testAiProviderConnection(new TestAiProviderConnection(provider.accountId, provider.id)),
		).catch(ignoreHandledError);
	}, [dispatch, provider.accountId, provider.id]);

	if (!canWrite && !canDelete) {
		return <div css={actionsRowStyle} />;
	}

	return (
		<div css={actionsRowStyle}>
			{canWrite ? (
				<Button
					type="button"
					size="sm"
					severity="secondary"
					aria-label={`Modifier le fournisseur ${provider.label}`}
					onClick={handleEditClick}
				>
					Modifier
				</Button>
			) : null}
			{canWrite ? (
				<Button
					type="button"
					size="sm"
					severity="secondary"
					aria-label={`Tester la connexion du fournisseur ${provider.label}`}
					onClick={handleTestClick}
				>
					Tester la connexion
				</Button>
			) : null}
			{canDelete ? (
				<Button
					type="button"
					size="sm"
					severity="danger"
					aria-label={`Supprimer le fournisseur ${provider.label}`}
					onClick={handleDeleteClick}
				>
					Supprimer
				</Button>
			) : null}
		</div>
	);
};

export default ProviderCardActions;
